/**
 * KinsCircle Phase 6D.2 — Legacy Collection Engine V1.
 *
 * STABILITY.md §13–§14:
 *   - PURE READ-ONLY orchestration over the existing Phase 6C/6D engines
 *     (`semanticIndexer`, `historianEngine`, `legacyTimelineEngine`).
 *   - NEVER mutates any memory row. NEVER writes to Supabase. NEVER
 *     persists generated collections — they are recomputed at runtime
 *     each time. (Persistence is opt-in via `legacyBookmarks.ts`.)
 *   - All collection items carry `sourceMemoryIds[]`, `memoryCount`,
 *     and `yearRange`, so claims can never be unanchored.
 *   - SAFE-MILESTONE allow-list ONLY. No death/illness/crime/military/
 *     politics/religion/medical inference.
 *   - Every helper early-returns safely when the master flag is OFF
 *     and validates memory existence before rendering.
 */

import { isLegacyCollectionsEnabled } from './featureFlag';
import {
  readIndexedMemories,
  buildMemoryClusters,
} from './semanticIndexer';
import {
  buildLegacyTimeline,
  type LegacyTimelineSlide,
} from './legacyTimelineEngine';
import {
  findMajorLifeMoments,
  findRepeatedFamilyTraditions,
  buildRelationshipTimeline,
  generateFamilyEraSummary,
  type FamilyEraSummary,
  type LifeMoment,
  type RepeatedTradition,
  type RelationshipTimelineEntry,
} from './historianEngine';
import type {
  SemanticMemoryRow,
  EmbeddingVector,
  MemoryCluster,
} from './memoryEmbeddingAdapter';

// ── Types ───────────────────────────────────────────────────────────────────

export type LegacyCollectionKind =
  | 'milestone'
  | 'person'
  | 'theme'
  | 'generation'
  | 'era'
  | 'custom';

export type LegacyCollection = {
  /** Stable id derived from the collection's defining axis. */
  id: string;
  kind: LegacyCollectionKind;
  /** Friendly title shown in the card header. */
  title: string;
  /** Short subtitle / preface, anchored only to real signals. */
  subtitle: string;
  /** "Cover" memory used as the visual anchor of the card. */
  coverMemoryId: string | null;
  /** Memory IDs belonging to this collection, in chronological order. */
  sourceMemoryIds: string[];
  memoryCount: number;
  yearRange: { from: number | null; to: number | null };
  /** Top tagged people across the collection (≤6, normalized). */
  topPeople: string[];
  /** Top safe themes across the collection (≤4). */
  topThemes: string[];
  /** Mandatory citation footer ("Based on N memor(y|ies) from YEAR–YEAR"). */
  citation: string;
};

export type LegacyCollectionRanking = {
  collection: LegacyCollection;
  /** 0..1 priority signal — higher = more impactful for this family. */
  score: number;
};

// ── Safe-milestone allow-list (mirrors the spec exactly) ────────────────────

const SAFE_MILESTONE_LABELS = new Set<string>([
  'birthday',
  'anniversary',
  'graduation',
  'wedding',
  'baby',
  'family reunion',
  'reunion',
  'homecoming',
  'vacation',
  'holiday',
  'achievement',
  'school',
  'sports',
  'celebration',
]);

// Themes we're willing to label collections with — defensive subset of the
// adapter's THEME_BOOST list, hand-filtered to exclude any term that could
// imply medical/criminal/political/religious content.
const SAFE_THEME_LABELS = new Set<string>([
  'birthday',
  'anniversary',
  'graduation',
  'reunion',
  'wedding',
  'baby',
  'newborn',
  'christmas',
  'thanksgiving',
  'easter',
  'hanukkah',
  'diwali',
  'ramadan',
  'beach',
  'vacation',
  'summer',
  'winter',
  'cookout',
  'barbecue',
  'basketball',
  'football',
  'baseball',
  'soccer',
  'dance',
  'recital',
  'prom',
  'homecoming',
]);

// ── Helpers ─────────────────────────────────────────────────────────────────

const yearRangeOf = (years: number[]): { from: number | null; to: number | null } => {
  const v = years.filter((y): y is number => typeof y === 'number');
  if (!v.length) return { from: null, to: null };
  return { from: Math.min(...v), to: Math.max(...v) };
};

const formatYearRange = (r: { from: number | null; to: number | null }): string => {
  if (r.from && r.to) return r.from === r.to ? `${r.from}` : `${r.from}–${r.to}`;
  if (r.from) return `${r.from}`;
  if (r.to) return `${r.to}`;
  return 'undated';
};

const buildCitation = (count: number, range: { from: number | null; to: number | null }): string =>
  `Based on ${count} memor${count === 1 ? 'y' : 'ies'} from ${formatYearRange(range)}.`;

const titleCase = (s: string): string =>
  s
    .split(/\s+/)
    .filter(Boolean)
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(' ');

const collectThemes = (rows: { vector: EmbeddingVector }[], limit = 4): string[] => {
  const acc: Record<string, number> = {};
  for (const r of rows) {
    for (const k of Object.keys(r.vector.terms)) {
      if (k.startsWith('@')) continue;
      if (!SAFE_THEME_LABELS.has(k)) continue;
      acc[k] = (acc[k] ?? 0) + r.vector.terms[k];
    }
  }
  return Object.keys(acc)
    .sort((a, b) => acc[b] - acc[a])
    .slice(0, limit);
};

const collectPeople = (rows: { vector: EmbeddingVector }[], limit = 6): string[] => {
  const acc: Record<string, number> = {};
  for (const r of rows) {
    for (const p of r.vector.people) acc[p] = (acc[p] ?? 0) + 1;
  }
  return Object.keys(acc)
    .sort((a, b) => acc[b] - acc[a])
    .slice(0, limit);
};

/** Pick a "cover" memory: prefer the one with the most tagged people *and* a year. */
const pickCoverMemoryId = (
  rows: { row: SemanticMemoryRow; vector: EmbeddingVector }[],
): string | null => {
  if (!rows.length) return null;
  let best = rows[0];
  let bestScore = -1;
  for (const r of rows) {
    const score = r.vector.people.length * 2 + (r.vector.year ? 1 : 0);
    if (score > bestScore) {
      bestScore = score;
      best = r;
    }
  }
  return best.row.id;
};

// ── Builders ────────────────────────────────────────────────────────────────

/**
 * Validate a candidate set of memory IDs against the live index. Drops any
 * id that doesn't resolve to a real indexed memory. Used by every public
 * builder to make sure no fabricated IDs leak into a collection.
 */
export const validateCollectionMemories = (memoryIds: string[]): string[] => {
  if (!isLegacyCollectionsEnabled()) return [];
  if (!memoryIds || !memoryIds.length) return [];
  const indexedIds = new Set(readIndexedMemories().map((m) => m.row.id));
  const seen = new Set<string>();
  const out: string[] = [];
  for (const id of memoryIds) {
    if (!indexedIds.has(id)) continue;
    if (seen.has(id)) continue;
    seen.add(id);
    out.push(id);
  }
  return out;
};

/**
 * Generic builder: given a validated set of memory IDs and metadata,
 * produce a `LegacyCollection`. Callers (the kind-specific builders
 * below) are responsible for selecting which memories belong.
 */
export const buildLegacyCollection = (input: {
  id: string;
  kind: LegacyCollectionKind;
  title: string;
  subtitle?: string;
  memoryIds: string[];
}): LegacyCollection | null => {
  if (!isLegacyCollectionsEnabled()) return null;
  const ids = validateCollectionMemories(input.memoryIds);
  if (!ids.length) return null;

  const indexed = readIndexedMemories();
  const byId = new Map(indexed.map((m) => [m.row.id, m]));
  const rows = ids
    .map((id) => byId.get(id))
    .filter((m): m is { row: SemanticMemoryRow; vector: EmbeddingVector } => !!m);
  if (!rows.length) return null;

  const sortedRows = [...rows].sort(
    (a, b) => (a.vector.year ?? 0) - (b.vector.year ?? 0),
  );
  const sortedIds = sortedRows.map((r) => r.row.id);
  const years = sortedRows
    .map((r) => r.vector.year)
    .filter((y): y is number => typeof y === 'number');
  const range = yearRangeOf(years);

  return {
    id: input.id,
    kind: input.kind,
    title: input.title,
    subtitle: input.subtitle ?? '',
    coverMemoryId: pickCoverMemoryId(sortedRows),
    sourceMemoryIds: sortedIds,
    memoryCount: sortedIds.length,
    yearRange: range,
    topPeople: collectPeople(sortedRows),
    topThemes: collectThemes(sortedRows),
    citation: buildCitation(sortedIds.length, range),
  };
};

/**
 * Milestone collections — birthdays, anniversaries, graduations, etc.,
 * grouped by safe-milestone label. Filters to the safe allow-list.
 */
export const buildMilestoneCollection = (limit = 6): LegacyCollection[] => {
  if (!isLegacyCollectionsEnabled()) return [];
  const moments: LifeMoment[] = findMajorLifeMoments(60);
  if (!moments.length) return [];

  // Bucket by lowercased label, but only safe ones.
  const buckets: Record<string, string[]> = {};
  for (const m of moments) {
    const k = m.label.toLowerCase();
    // Map the historian's labels onto the safe allow-list.
    let bucket: string | null = null;
    if (k.includes('birthday')) bucket = 'birthday';
    else if (k.includes('anniversary')) bucket = 'anniversary';
    else if (k.includes('graduation')) bucket = 'graduation';
    else if (k.includes('wedding')) bucket = 'wedding';
    else if (k.includes('baby')) bucket = 'baby';
    else if (k.includes('reunion')) bucket = 'family reunion';
    else if (k.includes('homecoming')) bucket = 'homecoming';
    if (!bucket || !SAFE_MILESTONE_LABELS.has(bucket)) continue;
    if (!buckets[bucket]) buckets[bucket] = [];
    buckets[bucket].push(m.memoryId);
  }

  const labelMap: Record<string, string> = {
    birthday: 'Birthday Celebrations',
    anniversary: 'Anniversaries',
    graduation: 'Graduation Years',
    wedding: 'Weddings',
    baby: 'New Baby Moments',
    'family reunion': 'Family Reunions',
    homecoming: 'Homecomings',
  };

  const out: LegacyCollection[] = [];
  for (const k of Object.keys(buckets)) {
    const c = buildLegacyCollection({
      id: `milestone:${k}`,
      kind: 'milestone',
      title: labelMap[k] ?? titleCase(k),
      subtitle: 'A collection of milestone moments your family has shared.',
      memoryIds: buckets[k],
    });
    if (c) out.push(c);
  }
  return out
    .sort((a, b) => b.memoryCount - a.memoryCount)
    .slice(0, Math.max(1, Math.min(20, limit)));
};

/**
 * Person journey collections — every memory in which a tagged person
 * appears, in chronological order. Requires ≥3 shared memories per person.
 */
export const buildPersonJourneyCollection = (limit = 6): LegacyCollection[] => {
  if (!isLegacyCollectionsEnabled()) return [];
  const timelines: RelationshipTimelineEntry[] = buildRelationshipTimeline();
  if (!timelines.length) return [];

  const out: LegacyCollection[] = [];
  for (const t of timelines) {
    if (t.appearances < 3) continue;
    const c = buildLegacyCollection({
      id: `person:${t.memberKey}`,
      kind: 'person',
      title: `Memories with ${titleCase(t.memberKey)}`,
      subtitle: `Every shared moment, in order.`,
      memoryIds: t.sourceMemoryIds,
    });
    if (c) out.push(c);
  }
  return out
    .sort((a, b) => b.memoryCount - a.memoryCount)
    .slice(0, Math.max(1, Math.min(20, limit)));
};

/**
 * Theme collections — Christmas, cookouts, basketball, etc. Powered by
 * the existing semantic clusterer; we only keep clusters whose label
 * lands in the safe theme allow-list.
 */
export const buildThemeCollection = (limit = 6): LegacyCollection[] => {
  if (!isLegacyCollectionsEnabled()) return [];
  const clusters: MemoryCluster[] = buildMemoryClusters({ minClusterSize: 3 });
  if (!clusters.length) return [];

  const out: LegacyCollection[] = [];
  for (const c of clusters) {
    // Safety: only label collections with allow-listed themes.
    const safeTopTerms = c.topTerms.filter((t) => SAFE_THEME_LABELS.has(t));
    if (!safeTopTerms.length) continue;
    const built = buildLegacyCollection({
      id: `theme:${c.id}`,
      kind: 'theme',
      title: c.label,
      subtitle: `${safeTopTerms.length} recurring theme${safeTopTerms.length === 1 ? '' : 's'} across your family memories.`,
      memoryIds: c.memoryIds,
    });
    if (built) out.push(built);
  }
  return out
    .sort((a, b) => b.memoryCount - a.memoryCount)
    .slice(0, Math.max(1, Math.min(20, limit)));
};

/**
 * Generation collections — recurring traditions or themes that appear
 * across ≥2 generations of indexed memories. We use repeated-tradition
 * clusters, which already require ≥3 occurrences across ≥2 distinct years,
 * and additionally require the year span to be ≥20 years (rough
 * single-generation gap). NO biological inference.
 */
export const buildGenerationCollection = (limit = 6): LegacyCollection[] => {
  if (!isLegacyCollectionsEnabled()) return [];
  const traditions: RepeatedTradition[] = findRepeatedFamilyTraditions();
  if (!traditions.length) return [];

  const out: LegacyCollection[] = [];
  for (const t of traditions) {
    const span =
      t.yearRange.from && t.yearRange.to ? t.yearRange.to - t.yearRange.from : 0;
    if (span < 20) continue;
    const c = buildLegacyCollection({
      id: `generation:${t.label.toLowerCase().replace(/\s+/g, '_')}`,
      kind: 'generation',
      title: `${t.label} — Across Generations`,
      subtitle: `A tradition shared across ${span}+ years of your family.`,
      memoryIds: t.sourceMemoryIds,
    });
    if (c) out.push(c);
  }
  return out
    .sort((a, b) => b.memoryCount - a.memoryCount)
    .slice(0, Math.max(1, Math.min(20, limit)));
};

/**
 * Convenience: rank an arbitrary list of collections by impact, where
 * impact = memoryCount × yearSpan. Ties broken by most recent.
 */
export const rankCollectionMoments = (
  collections: LegacyCollection[],
): LegacyCollectionRanking[] => {
  if (!collections.length) return [];
  const maxCount = Math.max(...collections.map((c) => c.memoryCount), 1);
  const maxSpan = Math.max(
    ...collections.map((c) => {
      if (c.yearRange.from && c.yearRange.to) return c.yearRange.to - c.yearRange.from;
      return 0;
    }),
    1,
  );
  return collections
    .map((c) => {
      const span =
        c.yearRange.from && c.yearRange.to ? c.yearRange.to - c.yearRange.from : 0;
      const score = (c.memoryCount / maxCount) * 0.65 + (span / maxSpan) * 0.35;
      return { collection: c, score };
    })
    .sort(
      (a, b) =>
        b.score - a.score ||
        (b.collection.yearRange.to ?? 0) - (a.collection.yearRange.to ?? 0),
    );
};

// ── Era + slide bridges ─────────────────────────────────────────────────────

/**
 * Build a runtime list of slides for a collection, ready to feed straight
 * into `<StoryPlaybackModal />`. We piggy-back on `buildLegacyTimeline`
 * which already knows how to safely format slide bodies, citations,
 * gradients, etc.
 */
export const collectionToPlaybackSlides = (
  collection: LegacyCollection,
  maxSlides = 14,
): LegacyTimelineSlide[] => {
  if (!isLegacyCollectionsEnabled()) return [];
  if (!collection?.sourceMemoryIds?.length) return [];
  const indexed = readIndexedMemories();
  const byId = new Map(indexed.map((m) => [m.row.id, m]));
  const ordered = collection.sourceMemoryIds
    .map((id) => byId.get(id))
    .filter((m): m is { row: SemanticMemoryRow; vector: EmbeddingVector } => !!m)
    .map((m) => ({ row: m.row, year: m.vector.year }));
  if (!ordered.length) return [];

  const tl = buildLegacyTimeline(ordered, { maxSlidesPerTimeline: maxSlides });
  return tl?.slides ?? [];
};

/** Pull the eras (decade buckets) — used by chapter-style cards. */
export const buildEraCollections = (limit = 6): LegacyCollection[] => {
  if (!isLegacyCollectionsEnabled()) return [];
  const eras: FamilyEraSummary[] = generateFamilyEraSummary();
  if (!eras.length) return [];
  const out: LegacyCollection[] = [];
  for (const e of eras) {
    const c = buildLegacyCollection({
      id: `era:${e.era.replace(/\s+/g, '_').toLowerCase()}`,
      kind: 'era',
      title: e.era,
      subtitle: e.themes.length
        ? `Themes: ${e.themes.filter((t) => SAFE_THEME_LABELS.has(t)).slice(0, 3).join(', ') || '—'}`
        : '',
      memoryIds: e.sourceMemoryIds,
    });
    if (c) out.push(c);
  }
  return out.slice(0, Math.max(1, Math.min(20, limit)));
};

// Re-export the slide type so callers don't need a second import.
export type { LegacyTimelineSlide };
